import React, { useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";

import useResignReasons from "../../../hooks/useResignReasons";
import { ResignReason } from "../../../types/onboarding";
import ResignReasonDialog from "./components/ResignReasonDialog";

/**
 * US5: resign-reason management. Lists active + inactive reasons; supports add,
 * edit-label, and deactivate (soft-delete).
 */
const ResignReasonsPage: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { reasons, loading, error, create, updateLabel, deactivate } =
    useResignReasons(toast);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<ResignReason | null>(null);

  const openAdd = () => {
    setEditing(null);
    setDialogVisible(true);
  };
  const openEdit = (reason: ResignReason) => {
    setEditing(reason);
    setDialogVisible(true);
  };

  const confirmDeactivate = (reason: ResignReason) => {
    confirmDialog({
      message: `Deactivate "${reason.display_label}"? It will no longer be selectable, but stays on past resignations that used it.`,
      header: "Deactivate reason",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => deactivate(reason.code),
    });
  };

  const statusBody = (reason: ResignReason) =>
    reason.active ? (
      <Tag severity="success" value="Active" />
    ) : (
      <Tag severity="danger" value="Inactive" />
    );

  const actionsBody = (reason: ResignReason) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-pencil"
        rounded
        text
        aria-label="Edit"
        onClick={() => openEdit(reason)}
      />
      {reason.active && (
        <Button
          icon="pi pi-ban"
          rounded
          text
          severity="danger"
          aria-label="Deactivate"
          onClick={() => confirmDeactivate(reason)}
        />
      )}
    </div>
  );

  return (
    <div className="card onboarding-page">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex align-items-center justify-content-between mb-3">
        <h2 className="m-0">Resign Reasons</h2>
        <Button label="Add reason" icon="pi pi-plus" onClick={openAdd} />
      </div>

      {error && (
        <Message
          severity="error"
          text="Resign reasons could not be loaded."
          className="mb-3 w-full"
        />
      )}

      <DataTable
        value={reasons}
        loading={loading}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50]}
        emptyMessage="No resign reasons yet."
      >
        <Column field="display_label" header="Reason" sortable />
        <Column field="code" header="Code" sortable />
        <Column header="Status" body={statusBody} />
        <Column header="" body={actionsBody} style={{ width: "8rem" }} />
      </DataTable>

      <ResignReasonDialog
        visible={dialogVisible}
        reason={editing}
        onHide={() => setDialogVisible(false)}
        onSubmit={(code, label) =>
          editing ? updateLabel(code, label) : create(code, label)
        }
      />
    </div>
  );
};

export default ResignReasonsPage;
